/**
 * FoxChat guided walkthrough runtime.
 *
 * Renders a top-center bar per step, highlights the target with the pulse
 * ring, and auto-advances as soon as the user completes the expected
 * interaction. Users can also navigate the flow manually with Back / Skip,
 * or cancel with X. A 60 s idle on any step pops an inline "Need help?"
 * prompt (open chat / keep waiting) instead of abandoning silently.
 *
 * wait_for handling:
 *   click          — one-shot click listener on the target.
 *   focus          — one-shot focus listener.
 *   value_entered  — `input` listener; advances when the field's value has
 *                    any non-whitespace character.
 *   page_change    — listens for our custom `foxchat:nav` event (dispatched
 *                    from the page-view tracker on every React Router
 *                    location change) plus the native `popstate`.
 *
 * Abandonment paths (cancel, navigate away when page_change wasn't expected)
 * log to foxchat_flow_abandonment so Saul can see which steps lose people.
 */
import { supabase } from "@/integrations/supabase/client";

export interface FlowStep {
  step: number;
  selector: string;
  instruction: string;
  wait_for: "click" | "focus" | "value_entered" | "page_change";
  /** v2 — optional URL path the step must be on for the target element to
   *  exist. When the user is off-page, we redirect the highlight to the
   *  nav link derived from this path and wait for navigation. */
  required_page?: string;
}

/** v2 — shape emitted by foxchat_kb rows that were seeded with the
 *  walkthrough spec: {step_num, text, highlight_id, required_page?, click_selector?}.
 *  The runtime normalises this to the internal FlowStep shape. */
interface FlowStepV2 {
  step_num: number;
  text: string;
  highlight_id: string;
  required_page?: string;
  click_selector?: string;
}

export interface Walkthrough {
  kb_id: string;
  title: string;
  steps: FlowStep[] | FlowStepV2[] | any[];
}

/** Normalise Saul's v2 flow_steps shape into the existing FlowStep internal
 *  shape. Leaves pre-normalised steps untouched. */
function normaliseSteps(steps: any[]): FlowStep[] {
  if (!Array.isArray(steps)) return [];
  return steps.map((s, idx) => {
    // Already v1 shape — just pass through, filling defaults.
    if (typeof s?.selector === "string" && typeof s?.instruction === "string") {
      return {
        step: typeof s.step === "number" ? s.step : idx + 1,
        selector: s.selector,
        instruction: s.instruction,
        wait_for: (s.wait_for || "click") as FlowStep["wait_for"],
        required_page: typeof s.required_page === "string" ? s.required_page : undefined,
      };
    }
    // v2 shape — translate.
    const highlightId: string = s?.highlight_id || "";
    const selector =
      s?.click_selector ||
      (highlightId ? `[data-foxchat-id="${highlightId}"]` : "");
    return {
      step: typeof s?.step_num === "number" ? s.step_num : idx + 1,
      selector,
      instruction: s?.text || "",
      // v2 rows without click_selector get a page_change fallback so the
      // runtime still advances when the user moves to the target page; with
      // click_selector set we default to "click" so a click on the target
      // advances automatically (no Next button needed).
      wait_for: s?.click_selector ? "click" : "click",
      required_page: typeof s?.required_page === "string" ? s.required_page : undefined,
    };
  });
}

const IDLE_TIMEOUT_MS = 60_000;
const HOST_ID = "foxchat-walkthrough-host";
let activeCleanup: (() => void) | null = null;
let startedAt: number | null = null;
let sessionIdCache: string | null = null;

function getSessionId(): string {
  if (sessionIdCache) return sessionIdCache;
  try {
    sessionIdCache = sessionStorage.getItem("foxchat_session_id");
  } catch { /* ignore */ }
  if (!sessionIdCache) sessionIdCache = "anon-" + Math.random().toString(36).slice(2);
  return sessionIdCache;
}

async function logAbandonment(kbId: string, stepNumber: number, totalSteps: number, reason: string) {
  try {
    const { data: u } = await supabase.auth.getUser();
    await supabase.from("foxchat_flow_abandonment" as any).insert({
      user_id: u?.user?.id || null,
      foxchat_kb_id: kbId,
      session_id: getSessionId(),
      total_steps: totalSteps,
      step_number: stepNumber,
      reason,
      started_at: startedAt ? new Date(startedAt).toISOString() : null,
    });
  } catch { /* best-effort */ }
}

function buildHost(): HTMLDivElement {
  let host = document.getElementById(HOST_ID) as HTMLDivElement | null;
  if (host) return host;
  host = document.createElement("div");
  host.id = HOST_ID;
  host.style.cssText = [
    "position:fixed",
    "top:16px",
    "left:50%",
    "transform:translateX(-50%)",
    "z-index:9999",
    "width:min(560px, calc(100vw - 32px))",
    "background:rgba(17, 17, 20, 0.96)",
    "border:1px solid rgba(251, 191, 36, 0.4)",
    "border-radius:12px",
    "box-shadow:0 10px 40px rgba(0,0,0,0.45)",
    "padding:14px 16px",
    "color:#f4f4f5",
    "font-family:ui-sans-serif,system-ui,sans-serif",
  ].join(";");
  document.body.appendChild(host);
  return host;
}

function destroyHost() {
  const host = document.getElementById(HOST_ID);
  if (host) host.remove();
}

function queryTarget(selector: string): HTMLElement | null {
  try {
    return document.querySelector<HTMLElement>(selector);
  } catch {
    return null;
  }
}

function waitFor(selector: string, timeoutMs = 6000): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const existing = queryTarget(selector);
    if (existing) return resolve(existing);
    const start = Date.now();
    const interval = setInterval(() => {
      const el = queryTarget(selector);
      if (el) { clearInterval(interval); resolve(el); }
      else if (Date.now() - start > timeoutMs) { clearInterval(interval); resolve(null); }
    }, 150);
  });
}

function pulse(el: HTMLElement) {
  el.classList.add("foxchat-pulse-ring");
  requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "center" }));
  window.setTimeout(() => el.classList.remove("foxchat-pulse-ring"), 3100);
}

// Button factory — keeps styles consistent across Back / Skip / X.
function makeButton(label: string, opts: { variant?: "ghost" | "solid" | "danger"; disabled?: boolean } = {}): HTMLButtonElement {
  const b = document.createElement("button");
  b.textContent = label;
  const base = "border:1px solid rgba(255,255,255,0.12);border-radius:6px;padding:4px 10px;font-size:11px;cursor:pointer;line-height:1";
  const variant = opts.variant === "danger"
    ? "background:transparent;color:#fca5a5"
    : opts.variant === "solid"
      ? "background:#f59e0b;color:#111;border-color:#f59e0b"
      : "background:transparent;color:#a1a1aa";
  b.style.cssText = base + ";" + variant + (opts.disabled ? ";opacity:0.4;cursor:not-allowed" : "");
  if (opts.disabled) b.disabled = true;
  return b;
}

function renderBar(
  host: HTMLDivElement,
  wt: Walkthrough,
  currentIdx: number,
  handlers: { onBack: () => void; onSkip: () => void; onExit: () => void },
) {
  const step = wt.steps[currentIdx];
  const pct = Math.round(((currentIdx + 1) / wt.steps.length) * 100);
  host.innerHTML = "";

  const row = document.createElement("div");
  row.style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:8px";
  const title = document.createElement("div");
  title.style.cssText = "flex:1;min-width:0;font-size:11px;color:#a1a1aa;text-transform:uppercase;letter-spacing:0.05em";
  title.textContent = `FoxChat walkthrough · Step ${step.step} of ${wt.steps.length}`;
  row.appendChild(title);

  const backBtn = makeButton("← Back", { disabled: currentIdx === 0 });
  backBtn.addEventListener("click", handlers.onBack);
  row.appendChild(backBtn);

  const skipBtn = makeButton("Skip →");
  skipBtn.addEventListener("click", handlers.onSkip);
  row.appendChild(skipBtn);

  const exitBtn = makeButton("×", { variant: "danger" });
  exitBtn.setAttribute("aria-label", "Exit walkthrough");
  exitBtn.style.minWidth = "28px";
  exitBtn.style.padding = "4px 8px";
  exitBtn.addEventListener("click", handlers.onExit);
  row.appendChild(exitBtn);

  host.appendChild(row);

  const instr = document.createElement("div");
  instr.style.cssText = "font-size:14px;line-height:1.4;margin-bottom:10px";
  instr.textContent = step.instruction;
  host.appendChild(instr);

  const barWrap = document.createElement("div");
  barWrap.style.cssText = "height:6px;background:rgba(255,255,255,0.08);border-radius:3px;overflow:hidden";
  const bar = document.createElement("div");
  bar.style.cssText = `height:100%;width:${pct}%;background:linear-gradient(90deg,#f59e0b,#fbbf24);transition:width 0.3s ease-out`;
  barWrap.appendChild(bar);
  host.appendChild(barWrap);
}

// "Need help?" prompt appended below the bar when the idle timer fires.
function renderIdlePrompt(host: HTMLDivElement, onOpenChat: () => void, onKeepWaiting: () => void) {
  const existing = host.querySelector("[data-idle-prompt]");
  if (existing) existing.remove();
  const box = document.createElement("div");
  box.setAttribute("data-idle-prompt", "1");
  box.style.cssText = "margin-top:10px;padding:8px 10px;border-radius:8px;background:rgba(251,191,36,0.08);border:1px solid rgba(251,191,36,0.2);display:flex;align-items:center;gap:8px;flex-wrap:wrap";
  const msg = document.createElement("div");
  msg.style.cssText = "flex:1;font-size:12px;color:#fcd34d";
  msg.textContent = "Stuck on this step? I can open FoxChat to help.";
  box.appendChild(msg);
  const yes = makeButton("Need help", { variant: "solid" });
  yes.addEventListener("click", onOpenChat);
  box.appendChild(yes);
  const no = makeButton("I got it");
  no.addEventListener("click", onKeepWaiting);
  box.appendChild(no);
  host.appendChild(box);
}

/**
 * Kick off a walkthrough. Returns a cleanup fn for the caller to stop it
 * imperatively (e.g. when the chat window closes).
 */
export async function startWalkthrough(rawWt: Walkthrough): Promise<() => void> {
  if (activeCleanup) activeCleanup();
  if (!rawWt?.steps?.length) return () => {};

  // v2 — normalise whatever shape the KB row used into the internal shape
  // used by attachStep. All downstream code works with FlowStep (v1).
  const wt: { kb_id: string; title: string; steps: FlowStep[] } = {
    kb_id: rawWt.kb_id,
    title: rawWt.title,
    steps: normaliseSteps(rawWt.steps as any[]),
  };
  if (!wt.steps.length) return () => {};

  startedAt = Date.now();
  const host = buildHost();
  let currentIdx = 0;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let cleanupStep: (() => void) | null = null;
  let finished = false;

  const resetIdle = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      // Surface a "Need help?" prompt instead of tearing down silently.
      renderIdlePrompt(host,
        () => {
          window.dispatchEvent(new CustomEvent("foxchat:open"));
          // Keep the walkthrough active — the chat and the step run in parallel.
          resetIdle();
        },
        () => {
          // Clear the prompt and keep waiting on the step's natural signal.
          const p = host.querySelector("[data-idle-prompt]");
          if (p) p.remove();
          resetIdle();
        },
      );
    }, IDLE_TIMEOUT_MS);
  };

  // When a gated step fires its navigation, we expect ONE nav event that
  // brings us onto the required_page. We absorb that event by bumping this
  // counter in the gated handler; top-level onNav decrements-and-skips.
  let expectedGateNavs = 0;

  const finish = (reason: "completed" | "cancelled" | "idle" | "navigated") => {
    if (finished) return;
    finished = true;
    if (idleTimer) clearTimeout(idleTimer);
    if (cleanupStep) cleanupStep();
    destroyHost();
    window.removeEventListener("popstate", onNav);
    window.removeEventListener("foxchat:nav", onNav as any);
    activeCleanup = null;
    if (reason !== "completed") {
      void logAbandonment(wt.kb_id, wt.steps[currentIdx]?.step ?? currentIdx + 1, wt.steps.length, reason);
    }
  };

  // Navigation detector — native popstate AND our custom foxchat:nav event
  // (dispatched by the page-view tracker on React Router changes).
  const onNav = () => {
    if (expectedGateNavs > 0) {
      expectedGateNavs--;
      return; // user just resolved a required_page gate — don't abandon.
    }
    const step = wt.steps[currentIdx];
    if (step?.wait_for !== "page_change") {
      finish("navigated");
    }
  };
  window.addEventListener("popstate", onNav);
  window.addEventListener("foxchat:nav", onNav as any);

  const advance = () => {
    if (cleanupStep) { cleanupStep(); cleanupStep = null; }
    currentIdx++;
    if (currentIdx >= wt.steps.length) {
      renderComplete(host);
      setTimeout(() => finish("completed"), 1500);
      return;
    }
    attachStep();
  };

  const back = () => {
    if (currentIdx === 0) return;
    if (cleanupStep) { cleanupStep(); cleanupStep = null; }
    currentIdx--;
    attachStep();
  };

  const attachStep = async () => {
    const step = wt.steps[currentIdx];

    // required_page gate — if the step expects a specific path and we're not
    // on it, swap the target to the sidebar nav link derived from that path
    // and rewrite the instruction. When the user navigates, re-render the
    // step (attachStep re-runs on foxchat:nav via the page_change listener).
    const onRequiredPage = !step.required_page ||
      window.location.pathname === step.required_page ||
      window.location.pathname.startsWith(step.required_page + "/");
    let effectiveSelector = step.selector;
    let effectiveInstruction = step.instruction;
    let effectiveWait: FlowStep["wait_for"] = step.wait_for;
    if (!onRequiredPage && step.required_page) {
      // Derive `nav-<slug>` from the last path segment (works for
      // /dashboard/dealflow → nav-dealflow, /dashboard/scans → nav-scans).
      const parts = step.required_page.split("/").filter(Boolean);
      const navSlug = parts[parts.length - 1] || "";
      effectiveSelector = `[data-foxchat-id="nav-${navSlug}"]`;
      effectiveInstruction = `First, go to ${navSlug.replace(/-/g, " ")} — I'll highlight it in the nav. ${step.instruction}`;
      effectiveWait = "page_change";
    }

    renderBar(host, { ...wt, steps: wt.steps.map((s, i) => i === currentIdx ? { ...s, instruction: effectiveInstruction } : s) }, currentIdx, {
      onBack: back,
      onSkip: advance,
      onExit: () => finish("cancelled"),
    });
    resetIdle();

    const el = await waitFor(effectiveSelector, 8000);
    if (!el) {
      const warn = document.createElement("div");
      warn.style.cssText = "margin-top:8px;font-size:11px;color:#fca5a5";
      warn.textContent = "Can't find that element on the page. Skip to try the next step.";
      host.appendChild(warn);
      return;
    }
    pulse(el);

    // When the required_page gate rewrote the target to a nav link, the
    // user clicking that nav link should NOT advance — it should re-render
    // the step with the real on-page target. We detect "gated" via
    // effectiveWait being "page_change" with a required_page on the step.
    const gated = !onRequiredPage && !!step.required_page;
    if (gated) {
      // Pre-register ONE incoming nav event as "expected" so top-level onNav
      // doesn't treat it as abandonment. The gated re-attach handler will
      // re-run attachStep after the router settles.
      expectedGateNavs = 1;
      const onClick = () => {
        // Let the router finish navigation before re-rendering.
        setTimeout(() => { if (!finished) attachStep(); }, 300);
      };
      const onNavReattach = () => {
        setTimeout(() => { if (!finished) attachStep(); }, 100);
      };
      el.addEventListener("click", onClick, { once: true });
      window.addEventListener("popstate", onNavReattach, { once: true });
      window.addEventListener("foxchat:nav", onNavReattach as any, { once: true });
      cleanupStep = () => {
        el.removeEventListener("click", onClick);
        window.removeEventListener("popstate", onNavReattach);
        window.removeEventListener("foxchat:nav", onNavReattach as any);
      };
    } else {
      // Normal step — use the step's wait_for semantics as before.
      switch (step.wait_for) {
        case "click": {
          const onClick = () => advance();
          el.addEventListener("click", onClick, { once: true });
          cleanupStep = () => el.removeEventListener("click", onClick);
          break;
        }
        case "focus": {
          const onFocus = () => advance();
          el.addEventListener("focus", onFocus, { once: true });
          cleanupStep = () => el.removeEventListener("focus", onFocus);
          break;
        }
        case "value_entered": {
          // Advance as soon as the field has any non-whitespace input — matches
          // the spec and feels snappier than waiting for blur.
          const onInput = () => {
            const v = (el as HTMLInputElement | HTMLTextAreaElement).value || "";
            if (v.trim().length > 0) {
              el.removeEventListener("input", onInput);
              advance();
            }
          };
          el.addEventListener("input", onInput);
          cleanupStep = () => el.removeEventListener("input", onInput);
          break;
        }
        case "page_change": {
          const onPageChange = () => advance();
          // Either a native popstate or our custom nav dispatch will trigger.
          window.addEventListener("popstate", onPageChange, { once: true });
          window.addEventListener("foxchat:nav", onPageChange as any, { once: true });
          cleanupStep = () => {
            window.removeEventListener("popstate", onPageChange);
            window.removeEventListener("foxchat:nav", onPageChange as any);
          };
          break;
        }
        default: {
          // Unknown wait_for — fall back to click so a bad row never deadlocks.
          const onClick = () => advance();
          el.addEventListener("click", onClick, { once: true });
          cleanupStep = () => el.removeEventListener("click", onClick);
        }
      }
    }

    // Any user activity resets the idle timer.
    const activity = () => resetIdle();
    document.addEventListener("mousemove", activity, { passive: true });
    document.addEventListener("keydown", activity, { passive: true });
    const prev = cleanupStep;
    cleanupStep = () => {
      if (prev) prev();
      document.removeEventListener("mousemove", activity);
      document.removeEventListener("keydown", activity);
    };
  };

  attachStep();

  activeCleanup = () => finish("cancelled");
  return activeCleanup;
}

function renderComplete(host: HTMLDivElement) {
  host.innerHTML = `<div style="display:flex;align-items:center;gap:10px;font-size:14px"><span style="font-size:18px">✓</span><span>Walkthrough complete.</span></div>`;
  host.style.borderColor = "rgba(16, 185, 129, 0.5)";
}
